import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { message, Popover } from "antd";
import TabsWithBadge from "../../common/tabsWithBadge";
import { Button } from "../../common/button";

import * as conversationService from "V2.0/services/conversationService";

import authService from "../../services/authService";

import {
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";

import { useSelectedConversation } from "./models/useSelectedConversation";
import { useRetryExtration } from "./models/useRetryExtraction";
import useTurnMetrics from "./models/useTurnMetrics";

import { SearchConversation } from "./components/search";
import { Conversations } from "./components/conversations";
import { ConversationDetails } from "./components/conversation_details";
import { TurnMetricsCard } from "./components/turn_metrics/TurnMetricsCard";
import { ConversationDeletion } from "./components/conversation_deletion";
import { ThreadRecording } from "./components/Thread_recording";
import {
  XMarkIcon,
  ChatBubbleLeftRightIcon,
  TagIcon,
  PhoneIcon,
} from "@heroicons/react/24/outline";
import { usePaginatedConversations } from "./models/usePaginatedConversations";
import { ExtractedData } from "./components/extractions/ExtractedData";
import ExtractionDetails from "./components/extractions/ExtractionDetails";
import { MdOutlineEmojiEmotions } from "react-icons/md";
import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import { useAssignedConversations } from "./models/useAssignedConversations";
import { useDebouncedSearch } from "./models/useDebouncedSearch";
import { FunnelIcon } from "@heroicons/react/24/solid";
import ConversationFilters from "./components/conversation_filters/ConversationFilters";
import { useAssistants } from "V2.0/HomePages/Assistants";
import { FilterApplied } from "V2.0/HomePages/HomeCampaign";
import dayjs from "dayjs";
import ConversationTags from "./components/tags/ConversationTags";
import usePaginatedHumanAssignedConversations from "./models/usePaginatedHumanAssignedConversations";
import { CONVERSATIONS_PER_PAGE } from "./constants";

const formatFollowUpTime = (timestamp) => {
  return dayjs(Number(timestamp)).format("MMM DD, YYYY");
};

const TABS = {
  ASSIGNED_TO_ME: "Assigned",
  ALL: "All",
};

export function ChatEditor(props) {
  const { conversation, updateLocalMessage, onSuccess, onSend } = props;

  const [openPicker, setOpenPicker] = useState(false);

  const ref = useRef();

  const handleSend = useCallback(() => {
    const data = {
      toNumber: conversation.userIdentifier,
      message: ref.current.textContent,
    };

    updateLocalMessage(ref.current.textContent);
    ref.current.textContent = "";

    onSend(conversation.workspace, conversation.assistant, data, onSuccess);
  }, [
    conversation.workspace,
    conversation.assistant,
    onSuccess,
    updateLocalMessage,
  ]);

  useEffect(() => {
    const enterKeyEventListener = (event) => {
      event.stopPropagation();
      if (event.keyCode === 13 && !event.shiftKey) {
        event.preventDefault();
        handleSend();
      }
    };

    ref.current.addEventListener("keydown", enterKeyEventListener);

    return () => {
      if (ref?.current) {
        ref.current.removeEventListener("keydown", enterKeyEventListener);
      }
    };
  }, [handleSend]);

  useEffect(() => {
    let listener;
    if (openPicker) {
      listener = () => setOpenPicker(false);
      window.addEventListener("click", listener);
    }

    return () => {
      window.removeEventListener("click", listener);
    };
  }, [openPicker]);

  return (
    <>
      <div className="flex flex-none border-t shadow items-center justify-end p-4 gap-x-4">
        <div className="w-full border bg-white p-2 rounded-lg min-w-0">
          <div
            ref={ref}
            contentEditable="true"
            className="border-none focus:outline-none whitespace-pre break-words min-h-[1rem] max-h-[8rem] text-wrap overflow-scroll"
          />
        </div>
        <div className="relative">
          <Popover
            color="none"
            open={openPicker}
            trigger="click"
            content={
              <Picker
                theme="light"
                data={data}
                onEmojiSelect={(e) => {
                  ref.current.innerText = ref.current.innerText + e.native;
                  setOpenPicker(false);
                }}
                openPicker={openPicker}
              />
            }
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                setOpenPicker((prevPicker) => !prevPicker);
              }}
            >
              <MdOutlineEmojiEmotions className="size-6" />
            </button>
          </Popover>
        </div>
        <div>
          <Button onClick={handleSend}>Send</Button>
        </div>
      </div>
    </>
  );
}

export function Threads() {
  const {
    projects,
    activeProject,
    settings,
    fetchSettings,
    searchParams,
    setSearchParams,
    projectId,
    customers: users,
    isCustomersLoading,
    isViewOnly
  } = useOutletContext();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  // const { conversations, fetchConversations } = useConversations();
  const { searchTerm, updateSearchTerm } = useDebouncedSearch();
  const { selectedConversation, updateSelectedConversation } =
    useSelectedConversation();
  const {
    totalConversations,
    conversations: allConversations,
    totalPages,
    loading,
    isMoreLoading,
    showLoadMoreConversation,
    fetchPaginatedConversations,
    loadMoreConversations,
    resetConversations,
    startDate,
    endDate,
    updateDate,
    assistantId,
    updateAssistantId,
    extractedData,
    updateExtractedData,
    callDuration,
    updateCallDuration,
    assignedTags,
    updateAssignedTags,
    untagged,
    updateUntagged,
    resetFilters,
    resetDate,
    resetAssistantId,
    resetExtractedData,
    resetCallDuration,
    resetAssignedTags,
    resetUntagged,
    syncSelectedConversation,
    pushConversation,
  } = usePaginatedConversations(
    projectId,
    searchTerm,
    updateSelectedConversation
  );

  // const {
  //   assignedToMeConversations,
  //   isLoading,
  //   fetchAssignedConversations,
  //   fetchAssignedConversationsBySearchTerm,
  //   resetAssignedConversations,
  //   syncSelectedAssignedConversation,
  //   pushAssignedConversation,
  // } = useAssignedConversations(projectId, searchTerm);

  const {
    assignedConversations,
    totalAssignedConversations,
    fetchPaginatedHumanAssignedConversations,
    showLoadMoreAssignedConversations,
    syncSelectedAssignedConversation,
    pushAssignedConversation,
    resetPaginatedHumanAssignedConversation,
    loadMoreHumanAssignedConversations,
    isAssignedConversationsLoading,
    isMoreAssignedConversationsLoading,
  } = usePaginatedHumanAssignedConversations(projectId);

  const { handleRetry } = useRetryExtration();

  const [callRecordingUrl, setCallRecordingURL] = useState("");
  const [activeTab, setActiveTab] = useState(
    searchParams.get("tab") || TABS.ALL
  );

  const [openFilters, setOpenFilters] = useState(false);
  const { assistants, fetchAssistants } = useAssistants(projectId);

  // Turn metrics (staff-only per-assistant flag). assistantsRecord maps id → name, so read
  // the flag from the raw assistants list instead.
  const selectedConversationAssistantId =
    typeof selectedConversation?.assistant === "object"
      ? selectedConversation?.assistant?._id
      : selectedConversation?.assistant;
  const selectedAssistant = useMemo(
    () => assistants.find((item) => item._id === selectedConversationAssistantId),
    [assistants, selectedConversationAssistantId],
  );
  const turnMetrics = useTurnMetrics({
    workspaceId: selectedConversation?.workspace,
    conversationId: selectedConversation?._id,
    assistantEnabled: Boolean(selectedAssistant?.visibilitySettings?.turnMetricsEnabled),
    refreshKey: selectedConversation?.messages?.length ?? 0,
  });

  const [isOpen, setIsOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [isLoadingRecording, setIsLoadingRecording] = useState(false);
  const [scrollToView, setScrollToView] = useState(true);
  const searchRef = useRef(null);
  const interval = useRef(null);
  const didMountRef = useRef(false);
  const conversationsContainerRef = useRef(false);

  const showThreadRecording = useMemo(() => {
    const isCallConversationSelected =
      selectedConversation?.sourceOfConversation === "INBOUNDVOICE" ||
      selectedConversation?.sourceOfConversation === "OUTBOUNDVOICE";
    const isCallRecordingUrlAvailable =
      isCallConversationSelected &&
      selectedConversation?.callDetails?.callRecordingUrl;

    return isCallRecordingUrlAvailable;
  }, [selectedConversation]);

  const tabs = useMemo(() => {
    return Object.values(TABS).map((tab) => {
      let count = totalConversations;
      if (tab === TABS.ASSIGNED_TO_ME) {
        count = totalAssignedConversations;
      }
      return {
        name: tab,
        current: tab === activeTab,
        count,
      };
    });
  }, [activeTab, totalConversations, totalAssignedConversations]);

  // const tabs = useMemo(() => {
  //   return Object.values(TABS).map((tab) => {
  //     let count = totalConversations;
  //     if (tab === TABS.ASSIGNED_TO_ME) {
  //       count = assignedToMeConversations?.length;
  //     }
  //     return {
  //       name: tab,
  //       current: tab === activeTab,
  //       count,
  //     };
  //   });
  // }, [activeTab, totalConversations, assignedToMeConversations]);

  // useEffect(() => {
  //   if (Object.keys(selectedConversation).length > 0) {
  //     setShowDetails(true);
  //   } else {
  //     setShowDetails(false);
  //   }
  // }, [selectedConversation]);

  function resetToThreadsHome() {
    updateSelectedConversation({});
    setShowDetails(false);
    navigate({pathname: "/threads", search: location.search});
  }

  useEffect(() => {
    if (conversationsContainerRef.current) {
      conversationsContainerRef.current.scrollTop = 0;
    }
  }, [
    startDate,
    endDate,
    searchTerm,
    extractedData,
    untagged,
    assignedTags,
    callDuration,
  ]);

  useEffect(() => {
    if (projectId) {
      fetchAssistants();
      fetchPaginatedConversations(1);
      // fetchAssignedConversations();
      fetchPaginatedHumanAssignedConversations(1);
    }

    return () => {
      searchRef.current?.reset();
      resetConversations();
      // resetAssignedConversations();
      resetPaginatedHumanAssignedConversation();
      updateSelectedConversation({});
      setShowDetails(false);
      // navigate(`/threads?projectId=${projectId}&tab=${activeTab}`);
    };
  }, [projectId]);

  useEffect(() => {
    if (settings?.refreshConversations && !interval?.current) {
      interval.current = setInterval(() => {
        fetchPaginatedConversations(1, CONVERSATIONS_PER_PAGE, false);
        // fetchAssignedConversations();
        fetchPaginatedHumanAssignedConversations(
          1,
          CONVERSATIONS_PER_PAGE,
          false
        );
      }, 30000);
    }

    return () => {
      if (interval.current) {
        clearInterval(interval.current);
      }
    };
  }, [settings?.refreshConversations]);

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }

    if (projectId && searchTerm) {
      resetToThreadsHome();
      fetchPaginatedConversations(1);
      fetchPaginatedHumanAssignedConversations(1);

      if (settings?.refreshConversations && !interval?.current) {
        interval.current = setInterval(() => {
          fetchPaginatedConversations(1, CONVERSATIONS_PER_PAGE, false);
          // fetchAssignedConversations();
          fetchPaginatedHumanAssignedConversations(
            1,
            CONVERSATIONS_PER_PAGE,
            false
          );
        }, 30000);
      }
    }

    if (projectId && !searchTerm) {
      resetToThreadsHome();
      fetchPaginatedConversations(1);
      fetchPaginatedHumanAssignedConversations(1);
      if (interval.current) {
        clearInterval(interval.current);
        interval.current = null;
      }
    }
  }, [searchTerm]);

  useEffect(() => {
    setCallRecordingURL("");
  }, [selectedConversation?._id]);

  useEffect(() => {
    return () => {
      if (callRecordingUrl) {
        URL.revokeObjectURL(callRecordingUrl);
      }
    };
  }, [callRecordingUrl]);

  useEffect(() => {
    if (!projectId || !activeTab) {
      return;
    }

    const paramProjectId = searchParams.get("projectId");
    const paramTab = searchParams.get("tab");

    if (paramProjectId && paramTab) {
      return;
    }

    if (!paramProjectId || !paramTab) {
      const updatedSearchParams = new URLSearchParams(searchParams);

      updatedSearchParams.set("projectId", projectId);
      updatedSearchParams.set("tab", activeTab);

      setSearchParams(updatedSearchParams, { replace: true });
    }
  }, [searchParams, projectId, activeTab]);

  const handleGetRecordings = async () => {
    try {
      setIsLoadingRecording(true);
      const response = await conversationService.getRecordingLink({
        workspaceId: selectedConversation.workspace,
        conversationId: selectedConversation._id,
      });
      const blob = response.data;
      const audioUrl = URL.createObjectURL(blob);
      setCallRecordingURL(audioUrl);
    } catch (e) {
      message.error(
        e.response.data?.message ||
          e.response.data?.msg ||
          e.response.data?.error ||
          "Failed to download Recording"
      );
    } finally {
      setIsLoadingRecording(false);
    }
  };

  const refetchAllAndAssignedConversations = () => {
    fetchPaginatedConversations(1, CONVERSATIONS_PER_PAGE, false);
    // fetchAssignedConversations(false);
    fetchPaginatedHumanAssignedConversations(1, CONVERSATIONS_PER_PAGE, false);
  };

  useEffect(() => {
    const listener = (event) => {
      fetchPaginatedConversations(1, CONVERSATIONS_PER_PAGE, false);
      // fetchAssignedConversations(false);
      fetchPaginatedHumanAssignedConversations(
        1,
        CONVERSATIONS_PER_PAGE,
        false
      );
    };

    navigator.serviceWorker.addEventListener("message", listener);
    return () =>
      navigator.serviceWorker.removeEventListener("message", listener);
  }, [
    fetchPaginatedConversations /*fetchAssignedConversations*/,
    ,
    fetchPaginatedHumanAssignedConversations,
  ]);

  function handleSearch(event) {
    const term = event.target.value || "";
    // const sanitizedTerm = term.replace(/\+/g, "");
    updateSearchTerm(term.trim());
  }

  const appliedFiltersLength = [
    Boolean(startDate),
    Boolean(assistantId),
    Boolean(Object.keys(extractedData).length),
    untagged || assignedTags.length > 0,
    Number(callDuration) > 0,
  ].reduce((acc, val) => {
    if (val) {
      return acc + 1;
    } else return acc;
  }, 0);

  const assistant = useMemo(() => {
    if (assistantId) {
      const _assistant = assistants.find((item) => item._id === assistantId);
      return _assistant;
    }

    return {};
  }, [assistantId, assistants]);

  const assistantsRecord = useMemo(() => {
    if (Boolean(assistants.length)) {
      const _assistantsRecord = assistants.reduce((acc, assistant) => {
        return {
          ...acc,
          [assistant._id]: assistant.name,
        };
      }, {});

      return _assistantsRecord;
    }

    return {};
  }, [assistants]);

  return (
    <>
      <div className="flex h-[calc(100vh-16px)] rounded-lg">
        <div className="flex-none w-1/4 border-r border-gray-200 flex flex-col border-b ">
          <div className="flex flex-col gap-y-4 justify-between px-4 pt-4 border-b border-gray-200 sticky top-0">
            <h2 className="text-base font-semibold text-gray-800">Threads</h2>
            <div className="flex gap-x-4">
              <SearchConversation
                ref={searchRef}
                handleSearch={handleSearch}
              />
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
              <Button
                outline
                onClick={() => setOpenFilters(true)}
              >
                <FunnelIcon /> ({appliedFiltersLength})
              </Button>
              {startDate && endDate && (
                <FilterApplied
                  onClose={() => {
                    resetDate();
                    resetToThreadsHome();
                  }}
                >
                  {formatFollowUpTime(startDate)} -{" "}
                  {formatFollowUpTime(endDate)}
                </FilterApplied>
              )}
              {assistantId && (
                <FilterApplied
                  onClose={() => {
                    resetAssistantId();
                    resetExtractedData();
                    resetToThreadsHome();
                  }}
                >
                  <p className="text-wrap">{assistant?.name}</p>
                </FilterApplied>
              )}
              {Object.keys(extractedData).length > 0 && (
                <FilterApplied
                  onClose={() => {
                    resetExtractedData();
                    resetToThreadsHome();
                  }}
                >
                  Extractions ({Object.keys(extractedData).length})
                </FilterApplied>
              )}
              {Number(callDuration) > 0 && (
                <FilterApplied
                  onClose={() => {
                    resetCallDuration();
                    resetToThreadsHome();
                  }}
                >
                  <div className="gap-x-2 flex items-center">
                    <PhoneIcon className="size-4" /> {callDuration} secs
                  </div>
                </FilterApplied>
              )}
              {untagged && (
                <FilterApplied
                  onClose={() => {
                    resetUntagged();
                    resetToThreadsHome();
                  }}
                >
                  <div className="gap-x-2 flex items-center">
                    <TagIcon className="size-4" /> No Tags
                  </div>
                </FilterApplied>
              )}
              {assignedTags.length > 0 && (
                <FilterApplied
                  onClose={() => {
                    resetAssignedTags();
                    resetToThreadsHome();
                  }}
                >
                  <div className="gap-x-2 flex items-center">
                    <TagIcon className="size-4" />
                    {assignedTags.length}
                  </div>
                </FilterApplied>
              )}
            </div>
            <TabsWithBadge
              tabs={tabs}
              onTabChange={(e, { tab, index }) => {
                setActiveTab(tab.name);
                navigate(`/threads`);
                updateSelectedConversation({});
                setShowDetails(false);
              }}
            />
          </div>
          <div
            className="overflow-y-auto overflow-x-hidden flex-1"
            ref={conversationsContainerRef}
          >
            {activeTab === TABS.ALL && (
              <Conversations
                conversations={allConversations}
                updateSelectedConversation={updateSelectedConversation}
                showLoadMoreConversation={showLoadMoreConversation}
                loadMoreConversations={loadMoreConversations}
                loading={loading}
                assistants={assistantsRecord}
                workspaceId={projectId}
                setShowDetails={setShowDetails}
                setScrollToView={setScrollToView}
                conversationsContainerRef={conversationsContainerRef}
                isMoreLoading={isMoreLoading}
              />
            )}
            {activeTab === TABS.ASSIGNED_TO_ME && (
              <Conversations
                conversations={assignedConversations}
                updateSelectedConversation={updateSelectedConversation}
                showLoadMoreConversation={showLoadMoreAssignedConversations}
                loadMoreConversations={loadMoreHumanAssignedConversations}
                loading={isAssignedConversationsLoading}
                assistants={assistantsRecord}
                workspaceId={projectId}
                setShowDetails={setShowDetails}
                setScrollToView={setScrollToView}
                conversationsContainerRef={conversationsContainerRef}
                isMoreLoading={isMoreAssignedConversationsLoading}
              />
            )}
            {/* {activeTab === TABS.ASSIGNED_TO_ME && (
              <Conversations
                conversations={assignedToMeConversations}
                updateSelectedConversation={updateSelectedConversation}
                showLoadMoreConversation={false}
                loadMoreConversations={loadMoreConversations}
                loading={isLoading}
                assistants={assistantsRecord}
                workspaceId={projectId}
                setShowDetails={setShowDetails}
                setScrollToView={setScrollToView}
              />
            )} */}
          </div>
        </div>

        <Outlet
          context={{
            conversations:
              activeTab === TABS.ALL ? allConversations : assignedConversations,
            key: selectedConversation?._id,
            selectedConversation,
            updateSelectedConversation,
            onDeleteConversation: () => setIsOpen(true),
            onViewContactClick: () => setShowDetails(true),
            onRetryExtraction: () => {
              handleRetry(
                selectedConversation?.workspace,
                selectedConversation?._id
              );
            },
            fetchConversations: refetchAllAndAssignedConversations,
            resetSelectedConversation: resetToThreadsHome,
            loading:
              activeTab === TABS.ALL ? loading : isAssignedConversationsLoading,
            refreshConversations: settings?.refreshConversations,
            assistants: assistantsRecord,
            syncConversation: (conversation) => {
              if (activeTab === TABS.ALL) {
                syncSelectedConversation(conversation);
              } else {
                syncSelectedAssignedConversation(conversation);
              }
              updateSelectedConversation(conversation);
            },
            users,
            projectId,
            setShowDetails,
            pushConversation:
              activeTab === TABS.ALL
                ? pushConversation
                : pushAssignedConversation,
            scrollToView,
            setScrollToView,
            isViewOnly,
            turnMetrics
          }}
        />
        {/* <Outlet
          context={{
            conversations:
              activeTab === TABS.ALL
                ? allConversations
                : assignedToMeConversations,
            key: selectedConversation?._id,
            selectedConversation,
            updateSelectedConversation,
            onDeleteConversation: () => setIsOpen(true),
            onViewContactClick: () => setShowDetails(true),
            onRetryExtraction: () => {
              handleRetry(
                selectedConversation?.workspace,
                selectedConversation?._id
              );
            },
            fetchConversations: refetchAllAndAssignedConversations,
            resetSelectedConversation: () => updateSelectedConversation({}),
            loading: activeTab === TABS.ALL ? loading : isLoading,
            refreshConversations: settings?.refreshConversations,
            assistants: assistantsRecord,
            syncConversation: (conversation) => {
              if (activeTab === TABS.ALL) {
                syncSelectedConversation(conversation);
              } else {
                syncSelectedAssignedConversation(conversation);
              }
              updateSelectedConversation(conversation);
            },
            users,
            projectId,
            setShowDetails,
            pushConversation:
              activeTab === TABS.ALL
                ? pushConversation
                : pushAssignedConversation,
            scrollToView,
            setScrollToView,
          }}
        /> */}

        {!params?.conversationId && (
          <div className="flex flex-col my-auto items-center mx-auto">
            <ChatBubbleLeftRightIcon className="size-16" />
            <p>No Thread Selected</p>
          </div>
        )}

        {/* {selectedConversation?.sourceOfConversation ? (
          ConversationMessage[selectedConversation.sourceOfConversation]({
            key: selectedConversation._id,
            selectedConversation,
            onDeleteConversation: () => setIsOpen(true),
            onViewContactClick: () => setShowDetails(true),
            onRetryExtraction: () => {
              handleRetry(
                selectedConversation?.workspace,
                selectedConversation?._id
              );
            },
            fetchConversations: refetchAllAndAssignedConversations,
            resetSelectedConversation: () => updateSelectedConversation({}),
            loading,
            refreshConversations: settings?.refreshConversations,
            assistants: assistantsRecord,
            syncConversation: (conversation) => {
              if (activeTab === TABS.ALL) {
                syncSelectedConversation(conversation);
              } else {
                syncSelectedAssignedConversation(conversation);
              }
              updateSelectedConversation(conversation);
            },
            users,
          })
        ) : (
          <div className="flex flex-col my-auto items-center mx-auto">
            <ChatBubbleLeftRightIcon className="size-16" />
            <p>No Thread Selected</p>
          </div>
        )} */}

        {params?.conversationId && showDetails && (
          <div className="flex flex-col h-full w-1/3 shrink-0">
            <div className="flex gap-x-4 items-center min-h-16 p-2">
              <Button
                plain
                onClick={() => setShowDetails(false)}
              >
                <XMarkIcon className="size-6" />
              </Button>
              <p className="font-semibold text-gray-700">Details</p>
            </div>
            <div className="flex-1 h-full min-h-0 p-4 overflow-scroll">
              {showThreadRecording && (
                <div className="py-4">
                  <ThreadRecording
                    isLoadingRecording={isLoadingRecording}
                    callRecordingUrl={callRecordingUrl}
                    selectedItem={selectedConversation}
                    handleGetRecordings={handleGetRecordings}
                  />
                </div>
              )}

              <ConversationDetails
                conversation={selectedConversation}
                settings={settings}
              />
              <TurnMetricsCard turnMetrics={turnMetrics} />

              <ExtractionDetails conversation={selectedConversation} />

              <ConversationTags
                settings={settings}
                fetchSettings={fetchSettings}
                conversation={selectedConversation}
                syncConversation={(conversation) => {
                  if (activeTab === TABS.ALL) {
                    syncSelectedConversation(conversation);
                  } else {
                    syncSelectedAssignedConversation(conversation);
                  }
                  updateSelectedConversation(conversation);
                }}
                isViewOnly={isViewOnly}
              />
            </div>
          </div>
        )}
        <ConversationDeletion
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          conversationId={selectedConversation?._id}
          projectId={projects[activeProject]?._id}
          fetchConversations={() => {
            resetToThreadsHome();
            refetchAllAndAssignedConversations();
          }}
        />
      </div>
      {openFilters && (
        <ConversationFilters
          workspaceId={projectId}
          workspaceTags={settings?.workspaceTags}
          assistants={assistants}
          startDate={startDate}
          endDate={endDate}
          assistantId={assistantId}
          extractedData={extractedData}
          callDuration={callDuration}
          assignedTags={assignedTags}
          untagged={untagged}
          updateDate={updateDate}
          updateAssistantId={updateAssistantId}
          updateExtractedData={updateExtractedData}
          updateCallDuration={updateCallDuration}
          updateUntagged={updateUntagged}
          updateAssignedTags={updateAssignedTags}
          resetFilters={() => {
            resetFilters();
            resetToThreadsHome();
          }}
          onClose={() => setOpenFilters(false)}
          onApplyFilters={() => {
            fetchPaginatedConversations(1);
            resetToThreadsHome();
          }}
        />
      )}
    </>
  );
}
